import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, MARKETPLACE_CONSTANTS } from '../../../constants/common';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { MarketPlaceSpecimenCard } from '../../../types/interfaces/marketPlace.interfaces';
import { Genders, MarketPlaceSpecimenTagEnum } from '../../../types/enums';
import { centsToDollars } from '../../../services/account.service';
import { getAnimalAttachmentURL } from '../../../services/animal.service';
import './specimenCard.scss';

const FROZEN_BY_RSG_BADGE_SRC = '/images/frozen-by-rsg.svg';

type SpecimenTagBadge = { tagId: number; src: string; alt: string };

const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest('a, button, input, textarea, select, label'));

const SpecimenCard: FC<MarketPlaceSpecimenCard> = ({
  animalId,
  name,
  breed,
  species,
  quantity,
  price,
  gender,
  attachments,
  accountId,
  breederName,
  marketplaceSpecimenTagIds,
  onPreview,
  onAddToCart,
  disableAddToCart,
  onEdit,
  onDeactivate,
}) => {
  const priceInDollars = centsToDollars(price);
  const displayPrice = typeof priceInDollars === 'number' ? priceInDollars.toFixed(2) : priceInDollars;
  const priceUnitLabel = gender === Genders.Female ? MARKETPLACE_CONSTANTS.PER_STRAW : MARKETPLACE_CONSTANTS.PER_UNIT;
  const isClickable = Boolean(onPreview);

  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const tagBadges: SpecimenTagBadge[] = (marketplaceSpecimenTagIds ?? [])
    .map((tagId): SpecimenTagBadge | null => {
      if (tagId === MarketPlaceSpecimenTagEnum.FROZEN_BY_RSG) {
        return {
          tagId,
          src: FROZEN_BY_RSG_BADGE_SRC,
          alt: MARKETPLACE_CONSTANTS.FROZEN_BY_RSG,
        };
      }
      return null;
    })
    .filter((tagBadge): tagBadge is SpecimenTagBadge => tagBadge !== null);

  useEffect(() => {
    if (attachments?.length && animalId) {
      const url = getAnimalAttachmentURL(animalId, attachments[0].attachmentId, attachments[0].accessToken);
      const image = new Image();
      image.src = url;
      image.onload = () => {
        setImageUrl(url);
      };
      image.onerror = () => {
        setImageUrl(null);
      };
    } else {
      setImageUrl(null);
    }
  }, [attachments, animalId]);

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (!onPreview || isInteractiveTarget(event.target)) return;
    onPreview();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onPreview || isInteractiveTarget(event.target)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onPreview();
  };

  return (
    <article
      className={`specimen-card${isClickable ? ' specimen-card--clickable' : ''}`}
      aria-label={name ?? 'Specimen card'}
      onClick={isClickable ? handleCardClick : undefined}
      onKeyDown={isClickable ? handleCardKeyDown : undefined}
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}>
      <div className="specimen-card__image">
        <div className="specimen-card__image-container" style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}>
          {!imageUrl && <div className="specimen-card__image-placeholder">{ANIMAL_CONSTANTS.NO_IMAGE}</div>}
        </div>
      </div>

      <div className="specimen-card__body">
        <div className="specimen-card__header">
          <h3 className="specimen-card__name">{name ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</h3>

          {tagBadges.length > 0 && (
            <div className="specimen-card__badges">
              {tagBadges.map(tagBadge => (
                <span key={tagBadge.tagId} className="specimen-card__badge">
                  <img src={tagBadge.src} alt={tagBadge.alt} />
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="specimen-card__attributes">
          <div className="specimen-card__attr">
            <span className="specimen-card__label">{MARKETPLACE_CONSTANTS.BREED}</span>
            <span className="specimen-card__value">{breed ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</span>
          </div>
          <div className="specimen-card__attr">
            <span className="specimen-card__label">{MARKETPLACE_CONSTANTS.SPECIES}</span>
            <span className="specimen-card__value">{species ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</span>
          </div>
          <div className="specimen-card__attr">
            <span className="specimen-card__label">{MARKETPLACE_CONSTANTS.GENDER}</span>
            <span className="specimen-card__value">{gender ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</span>
          </div>
        </div>

        <div className="specimen-card__breeder-details">
          <div className="specimen-card__attr">
            <span className="specimen-card__label">{MARKETPLACE_CONSTANTS.BREEDER}</span>
            {accountId != null && breederName ? (
              <Link
                to={ROUTE_PATHS.APP_MARKETPLACE_VIEW + 'profile/' + accountId}
                className="specimen-card__value"
                title={breederName}>
                {breederName}
              </Link>
            ) : (
              <span className="specimen-card__value" title={breederName ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}>
                {breederName ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}
              </span>
            )}
          </div>
        </div>

        <div className="specimen-card__footer">
          <div className="specimen-card__price-stock">
            <div className="specimen-card__price">
              <span className="specimen-card__amount">${displayPrice ?? '0.00'}</span>
              <span className="specimen-card__per">{priceUnitLabel}</span>
            </div>
            <div className="specimen-card__stock">
              <span className="specimen-card__label">{MARKETPLACE_CONSTANTS.STOCK}</span>
              <span className="specimen-card__stock-value">{quantity ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</span>
            </div>
          </div>
          <div className="specimen-card__actions">
            {onEdit || onDeactivate ? (
              <>
                <button className="button medium blue" type="button" onClick={onEdit}>
                  {BUTTON_CONSTANTS.EDIT}
                </button>
                <button className="button red inverted" type="button" onClick={onDeactivate}>
                  {BUTTON_CONSTANTS.DEACTIVATE}
                </button>
              </>
            ) : (
              <>
                <button className="specimen-card__button specimen-card__button--preview button" type="button" onClick={onPreview}>
                  {BUTTON_CONSTANTS.VIEW_PROFILE}
                </button>
                <button
                  className="specimen-card__button specimen-card__button--add button"
                  type="button"
                  onClick={onAddToCart}
                  disabled={disableAddToCart}>
                  {BUTTON_CONSTANTS.ADD_TO_CART}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default SpecimenCard;
